import StayCash from './StayCash'
import { DateFactory } from 'rhg-vue-common'
import StayPoints from './StayPoints'
import MealPlan from './MealPlan'
import { language } from '../../infrastructure/Configuration'
import state from '../store/state'
import { v1 as uuidv1 } from 'uuid'

export default class Stay {
  static ONLY_CASH_WEIGTH = 1
  static CASH_AND_POINTS_WEIGHT = 1000000
  static ONLY_POINTS_WEIGHT = 1000000000

  static fromJSON(json) {
    const includesTaxes =
      json.averagePricing && json.averagePricing.taxIncluded
        ? json.averagePricing.taxIncluded
        : false
    const accumulated = state.dataQubit
    return new Stay({
      roomId: json.roomId,
      id: json.identifier,
      name: json.name,
      stayType: json.stayType,
      code: json.code,
      description: json.description,
      includesTaxes: includesTaxes,
      cash: StayCash.fromJSON(json, accumulated),
      points: StayPoints.fromJSON(json, accumulated),
      numberOfRoomsLeft: json.numberOfRoomsLeft,
      conditions: json.conditions?.cancellationPoliciesExtended?.types || [],
      memberOnly: json.memberOnly,
      mealPlan: json.mealPlan,
      filterMatches: json.filterMatches,
      mealPlanDto: json.mealPlanDto && MealPlan.fromJSON(json),
      accessible: json.accessible,
      undiscountedStay: json.undiscountedRate && Stay.fromJSON(json.undiscountedRate),
      orderBy: json.orderBy,
      rates: (json.rates || []).map(rate =>
        rate.points === '0'
          ? { id: rate.id, beginDate: rate.beginDate, endDate: rate.endDate }
          : rate
      ),
      eCertIsApplied: json.eCertIsApplied,
      comesFromTravelAgency: json.comesFromTravelAgency,
      checkInTime: json.checkInTime,
      checkOutTime: json.checkOutTime,
      occupancyIds: json.occupancyIds,
      hotelMarkedAsMemberOnlyInCms: json.hotelMarkedAsMemberOnlyInCms || false,
      jsonForIntegrateWithApi: { ...json },
      device: json.device,
      dealOfTheDay: json.dealOfTheDay,
      voucherApplied: json.voucherApplied,
      redeemable: json.redeemable,
      crowdDiscount: json.crowdDiscount,
      totalPriceWithTax: json.totalPriceWithTax,
      professional: json.professional
    })
  }

  constructor({
    roomId,
    id,
    name,
    filterMatches,
    stayType,
    code,
    includesTaxes,
    cash,
    points,
    conditions,
    numberOfRoomsLeft,
    memberOnly,
    mealPlan,
    mealPlanDto,
    accessible,
    undiscountedStay,
    orderBy,
    rates,
    eCertIsApplied,
    comesFromTravelAgency,
    checkInTime,
    checkOutTime,
    hotelMarkedAsMemberOnlyInCms,
    totalPoints,
    jsonForIntegrateWithApi,
    description,
    device,
    dealOfTheDay,
    voucherApplied,
    redeemable,
    crowdDiscount,
    totalPriceWithTax,
    professional,
    occupancyIds
  }) {
    this.roomId = roomId
    this.occupancyIds = occupancyIds || []
    this.id = id
    this.name = name
    this.stayType = stayType
    this.code = code
    this.includesTaxes = includesTaxes || false
    this.cash = cash || StayCash.empty()
    this.points = points || StayPoints.empty()
    this.numberOfRoomsLeft = numberOfRoomsLeft
    this.conditions = conditions || []
    this.memberOnly = memberOnly || false
    this.mealPlan = mealPlan || false
    this.mealPlanDto = mealPlanDto || []
    this.accessible = accessible || false
    this.undiscountedStay = undiscountedStay || null
    this.orderBy = orderBy
    this.rates = rates
    this.filterMatches = filterMatches
    this.eCertIsApplied = eCertIsApplied
    this.comesFromTravelAgency = comesFromTravelAgency
    this.checkInTime = checkInTime
    this.checkOutTime = checkOutTime
    this.hotelMarkedAsMemberOnlyInCms = hotelMarkedAsMemberOnlyInCms
    this.percentageOfDiscountPerNight =
      this.cash.percentageOfDiscountPerNight > this.points.percentageOfDiscountPerNight
        ? this.cash.percentageOfDiscountPerNight
        : this.points.percentageOfDiscountPerNight
    this.jsonForIntegrateWithApi = jsonForIntegrateWithApi
    this.uuid = uuidv1()
    this.description = description || ''
    this.device = device || null
    this.dealOfTheDay = dealOfTheDay
    this.voucherApplied = voucherApplied
    this.redeemable = redeemable
    this.crowdDiscount = crowdDiscount
    this.totalPriceWithTax = totalPriceWithTax
    this.professional = professional
  }

  isAccumulated = () => this.cash.isAccumulated() || this.points.isAccumulated()
  isDealOfTheDay = () => !!this.dealOfTheDay
  isRedeemable = () => !!this.redeemable
  isVoucherApplied = () => !!this.voucherApplied
  dealExpiryDate = () => this.dealOfTheDay?.endDate
  hasDiscount = () => this.cash.hasDiscount() || this.points.hasDiscount()
  isStandard = () => this.stayType === 'standard'
  isLoyalty = () => this.stayType === 'loyalty'
  isPackage = () => !!this.filterMatches?.packageStay
  isAccessible = () => !!this.filterMatches?.accessible
  hasBreakfastIncluded = () => !!this.filterMatches?.breakfastIncluded
  hasPoints = () => !!this.filterMatches?.points
  isFreeCancellation = () => !!this.filterMatches?.freeCancellation
  getFreeCancellationUpTo = () => {
    if (
      this.isFreeCancellation() &&
      this.jsonForIntegrateWithApi?.conditions?.cancellationPoliciesExtended?.freeCancellationUpTo
    ) {
      const pattern =
        language !== 'tr'
          ? DateFactory.DatePatterns.FULL_DATE_SHORT_TIME
          : DateFactory.DatePatterns.FULL_DATE_FULL_TIME
      return DateFactory.objectFromPattern(
        this.jsonForIntegrateWithApi.conditions.cancellationPoliciesExtended.freeCancellationUpTo,
        false
      ).format(pattern)
    }

    return ''
  }

  isOnlyCash = () => this.cash.hasCash() && !this.points.hasPoints()
  isCashAndPoints = () => this.cash.hasCash() && this.points.hasPoints()
  isOnlyPoints = () => !this.cash.hasCash() && this.points.hasPoints()
  weight = () =>
    this.isOnlyCash()
      ? this.cash.amountPerNight * Stay.ONLY_CASH_WEIGTH
      : this.isCashAndPoints()
        ? this.cash.amountPerNight * Stay.CASH_AND_POINTS_WEIGHT + this.points.amountPerNight
        : this.isOnlyPoints()
          ? this.points.amountPerNight * Stay.ONLY_POINTS_WEIGHT
          : null

  count = filter => filter.count(this) // TODO test

  applyFilter = filter => filter.match(this) // TODO test
  changeCurrency = selectedCurrency => {
    // TODO test
    return new Stay({
      ...this,
      cash: this.cash.convert(selectedCurrency),
      mealPlanDto: {
        ...this.mealPlanDto,
        price: {
          ...this.mealPlanDto.price,
          currency: selectedCurrency.code,
          amount: parseFloat(this.mealPlanDto.price.amount * selectedCurrency.exchangeRate).toFixed(
            2
          )
        }
      }
    })
  }

  isPayUponArrival = () => this.conditions.includes('PAY_AT_PROPERTY')
  canBook = customer => {
    return (
      this.isOnlyCash() ||
      (this.points.hasPoints() && customer.points >= this.points.totalAmountForAllNights)
    )
  }

  isEqualsTo = other => {
    return !!other && this.id === other.id
  }

  isNotEqualsTo = other => {
    return !!other && this.id !== other.id
  }

  isNotCreditCardNeeded = () => this.conditions.includes('NON_GUARANTEED')

  isMobileOnly = () => this.device === 'mobile'

  isMealPlan = () => ['RO', 'BB', 'HB', 'FB', 'AI'].includes(this.mealPlan)
}
